import { useRef, useEffect, useState } from 'react';

// ============================================================
// 편지지 레이아웃 — 프론트엔드 LetterPaper.tsx 와 동일한 원본 기준(1414×2000)
// ============================================================
const PAPER = {
  width: 1414,
  height: 2000,
  paddingLeft: 166,
  paddingRight: 166,
  paddingTop: 230,
  paddingBottom: 225,
  maxLines: 19,
};

// 편지지별 줄선 색상
const LINE_COLORS = {
  '1': 'rgb(162,186,239)',
  '2': 'rgb(230,210,166)',
  '3': 'rgb(171,116,82)',
  '4': 'rgb(114,53,17)',
};

// 폰트별 설정 (원본 기준 px)
const FONT_CONFIG = {
  NanumMyeongjo: { fontSize: 50, paddingTop: 230 },
  NanumMongDol: { fontSize: 65, paddingTop: 230 },
};
const DEFAULT_FONT_CONFIG = { fontSize: 65 };

// 폰트 로딩 캐시
const fontLoadPromises = {};

function loadFont(fontFamily) {
  if (fontLoadPromises[fontFamily]) return fontLoadPromises[fontFamily];

  const fontUrl = `/fonts/${fontFamily}.ttf`;
  const font = new FontFace(fontFamily, `url(${fontUrl})`);
  fontLoadPromises[fontFamily] = font.load().then((loaded) => {
    document.fonts.add(loaded);
    return loaded;
  });
  return fontLoadPromises[fontFamily];
}

export default function LetterCanvas({ text, background, fontFamily = 'NanumMyeongjo', scale = 4, canvasId }) {
  const canvasRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);

  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvasRef.current = canvas;
    canvas.id = canvasId;

    const fontConfig = FONT_CONFIG[fontFamily] || DEFAULT_FONT_CONFIG;
    const fontSize = fontConfig.fontSize;
    const padTop = fontConfig.paddingTop || PAPER.paddingTop;
    const padBottom = PAPER.paddingBottom;
    const padLeft = PAPER.paddingLeft;
    const padRight = PAPER.paddingRight;
    const contentHeight = PAPER.height - padTop - padBottom;
    const lineHeight = Math.round(contentHeight / PAPER.maxLines);
    const lineColor = LINE_COLORS[background] || LINE_COLORS['1'];

    canvas.width = PAPER.width * scale;
    canvas.height = PAPER.height * scale;

    const bgNum = background || '1';
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = `/letter-background/letter-background-noline-${bgNum}.png`;

    const doRender = () => {
      const ctx = canvas.getContext('2d');
      ctx.setTransform(scale, 0, 0, scale, 0, 0);

      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, PAPER.width, PAPER.height);

      if (img.complete && img.naturalWidth > 0) {
        ctx.drawImage(img, 0, 0, PAPER.width, PAPER.height);
      }

      for (let i = 0; i < PAPER.maxLines; i++) {
        const y = padTop + lineHeight * (i + 1);
        ctx.fillStyle = lineColor;
        ctx.fillRect(padLeft, y, PAPER.width - padLeft - padRight, 1);
      }

      ctx.fillStyle = '#333';
      ctx.font = `${fontSize}px ${fontFamily}`;
      ctx.textBaseline = 'top';

      const contentWidth = PAPER.width - padLeft - padRight;
      const rawLines = (text || '').split('\n');
      const wrappedLines = [];

      for (const rawLine of rawLines) {
        if (rawLine.length === 0) {
          wrappedLines.push('');
          continue;
        }
        let current = '';
        for (const char of rawLine) {
          const test = current + char;
          if (ctx.measureText(test).width > contentWidth) {
            wrappedLines.push(current);
            current = char;
          } else {
            current = test;
          }
        }
        if (current) wrappedLines.push(current);
      }

      for (let i = 0; i < Math.min(wrappedLines.length, PAPER.maxLines); i++) {
        const x = padLeft;
        const y = padTop + lineHeight * i + (lineHeight - fontSize) / 2;
        ctx.fillText(wrappedLines[i], x, y);
      }

      setImgSrc(canvas.toDataURL('image/png'));
    };

    const renderWhenReady = () => {
      loadFont(fontFamily).then(() => {
        if (img.complete) {
          doRender();
        } else {
          img.onload = doRender;
          img.onerror = doRender;
        }
      }).catch(() => {
        if (img.complete) {
          doRender();
        } else {
          img.onload = doRender;
          img.onerror = doRender;
        }
      });
    };

    renderWhenReady();
  }, [text, background, fontFamily, scale, canvasId]);

  return (
    <div className="letter-canvas-wrap">
      {imgSrc ? (
        <img src={imgSrc} alt="편지" id={canvasId} style={{ width: '100%', maxWidth: '400px', borderRadius: '8px' }} />
      ) : (
        <p>렌더링 중...</p>
      )}
    </div>
  );
}
